import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { KeyRound } from 'lucide-react';

import { parseTableParams, formatDate } from '../../utils/Utils';
import useMyEthicsReportApi from './config';
import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';

const Container = styled.div``;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 11px;
  font-weight: 600;
  background: ${({ $color }) => $color + '18'};
  color: ${({ $color }) => $color};
`;

const statusColors = {
  0: '#3B82F6', 1: '#8B5CF6', 2: '#F59E0B', 3: '#EF4444',
  4: '#10B981', 5: '#6B7280', 6: '#9CA3AF', 7: '#F97316',
};

export default function MyEthicsReportsPage() {
  const { t } = useTranslation();
  const history = useHistory();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const { fetching, data, pagination, columns: baseColumns } = useMyEthicsReportApi(queryParams);

  const statusLabels = useMemo(() => ({
    0: t('ethicsReport.statusNew'),
    1: t('ethicsReport.statusAcknowledged'),
    2: t('ethicsReport.statusUnderReview'),
    3: t('ethicsReport.statusInvestigation'),
    4: t('ethicsReport.statusActionTaken'),
    5: t('ethicsReport.statusClosed'),
    6: t('ethicsReport.statusUnfounded'),
    7: t('ethicsReport.statusAwaitingInfo'),
  }), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'title', label: t('ethicsReport.reportTitle'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'categoryName', label: t('ethicsReport.category'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'status',
      label: t('ethicsReport.status'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => (
          <StatusBadge $color={statusColors[v] || '#6B7280'}>
            {statusLabels[v] || '-'}
          </StatusBadge>
        ),
      },
    },
    {
      name: 'createDate',
      label: t('ethicsReport.date'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => v ? formatDate(v) : '-',
      },
    },
  ], [t, statusLabels]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, columns);
    setQueryParams(params);
  }, [columns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  // Mobildeki "Etik ve Uyum" kartıyla parite: ihbar bildirme ve anonim takip
  // ekranları menüden çıkarıldığı için (20e93f91, tekil giriş kararı) tek giriş
  // noktası bu sayfa — iki eylem de tablonun araç çubuğundan açılır.
  const handleNewButtonClick = useCallback(() => {
    history.push('/ethics-report');
  }, [history]);

  const handleTrackClick = useCallback(() => {
    history.push('/ethics-report/track');
  }, [history]);

  return (
    <Container>
      {useMemo(() => (
        <DataTable
          title={t('ethicsReport.myReports')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord
          newButtonLabel={t('ethicsReport.newReport')}
          onNewButtonClick={handleNewButtonClick}
          toolbarActions={(
            <Button variant="outline" onClick={handleTrackClick}>
              <KeyRound size={16} /> {t('menu.ethicsReportTrack')}
            </Button>
          )}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, filterList,
        handleFilterConfirm, handleDataStateChange,
        handleNewButtonClick, handleTrackClick,
      ])}
    </Container>
  );
}
